/**
 *****************************************
 *********** Dispatch Types **************
 *****************************************
 */
export const PREORDER_EVENTS_LOADING = "PREORDER_EVENTS_LOADING";
export const PREORDER_EVENTS_FAIL = "PREORDER_EVENTS_FAIL";
export const PREORDER_EVENTS_LIST = "PREORDER_EVENTS_LIST";
export const PREORDER_EVENTS_REGISTER = "PREORDER_EVENTS_REGISTER";
export const PREORDER_EVENTS_REFRESH = "PREORDER_EVENTS_REFRESH";
export const PREORDER_EVENTS_SET_FILTER = "PREORDER_EVENTS_SET_FILTER";
export const PREORDER_EVENT_DETAILS_SUCCESS = "PREORDER_EVENT_DETAILS_SUCCESS";
export const PREORDER_EVENT_ARCHIVE_SUCCESS = "PREORDER_EVENT_ARCHIVE_SUCCESS";
export const PREORDER_EVENT_UPDATE_SUCCESS = "PREORDER_EVENT_UPDATE_SUCCESS";
export const PREORDER_ITEM_ADD_SUCCESS = "PREORDER_ITEM_ADD_SUCCESS";
export const PREORDER_ITEM_UPDATE_SUCCESS = "PREORDER_ITEM_UPDATE_SUCCESS";
export const PREORDER_ITEM_QUANTITY_UPDATE_SUCCESS =
  "PREORDER_ITEM_QUANTITY_UPDATE_SUCCESS";
export const PREORDER_EVENT_SUGGESTED_SUCCESS =
  "PREORDER_EVENT_SUGGESTED_CLOSE_SUCCESS";
export const PREORDER_EVENT_SUGGESTED_LOADING =
  "PREORDER_EVENT_SUGGESTED_LOADING";
export const PREORDER_EVENT_SUGGESTED_FAIL = "PREORDER_EVENT_SUGGESTED_FAIL";
export const PREORDER_EVENT_SUGGESTED_SET_ITEMS =
  "PREORDER_EVENT_SUGGESTED_SET_ITEMS";
export const PREORDER_EVENT_SUGGESTED_CLEAR = "PREORDER_EVENT_SUGGESTED_CLEAR";
export const PREORDER_EVENT_ORDERS_LOADING = "PREORDER_EVENT_ORDERS_LOADING";
export const PREORDER_EVENT_ORDERS_SUCCESS = "PREORDER_EVENT_ORDERS_SUCCESS";
export const PREORDER_EVENT_ORDERS_FAIL = "PREORDER_EVENT_ORDERS_FAIL";
export const PREORDER_ORDER_UPDATE_LOADING = "PREORDER_ORDER_UPDATE_LOADING";
export const PREORDER_ORDER_UPDATE_SUCCESS = "PREORDER_ORDER_UPDATE_SUCCESS";
export const PREORDER_ORDER_UPDATE_FAIL = "PREORDER_ORDER_UPDATE_FAIL";
export const PREORDER_ITEMS_BULK_PUBLISH_LOADING = "PREORDER_ITEMS_BULK_PUBLISH_LOADING";
export const PREORDER_ITEMS_BULK_PUBLISH_SUCCESS = "PREORDER_ITEMS_BULK_PUBLISH_SUCCESS";
export const PREORDER_ITEMS_BULK_PUBLISH_FAIL = "PREORDER_ITEMS_BULK_PUBLISH_FAIL";

/**
 *****************************************
 ************* Object Types **************
 *****************************************
 */
export type PreorderEventType = {
  id: number;
  name: string;
  releaseDate: string;
  lastPreorderDate: string;
  archived: boolean;
  closed: boolean;
  postReleaseData?: any;
  items: any[];
  itemsCount?: number; // Computed field
};

export type PreorderEventList = {
  content: PreorderEventType[];
  first: boolean;
  last: boolean;
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
};

export type CreatePreorderEventRequest = {
  name: string;
  releaseDate: string;
  lastPreorderDate: string;
};

export type PreorderEventFilters = {
  name?: string;
  archived?: boolean; // Changed from isArchived to match API
  releaseDate?: string;
  lastPreorderDate?: string;
  page?: number;
  pageNumber?: number;
  size?: number;
  pageSize?: number;
  offset?: number;
  paged?: boolean;
  unpaged?: boolean;
  sort?: string[];
};

export type PreorderEventDetails = {
  archived: boolean;
  closed: boolean;
  id: number;
  items: PreorderEventItem[];
  lastPreorderDate: string;
  metrics: PreorderEventMetric[];
  name: string;
  postReleaseData: any;
  preorderVendorId: number;
  releaseDate: string;
};

export type PreorderEventItem = {
  id: number;
  name: string;
  sku: string;
  stock: number;
};

export type PreorderEventMetric = {
  items: PreorderEventMetricItem[];
  vendorId: number;
  vendorName: string;
};

export type PreorderEventMetricItem = {
  id: number;
  sku: string;
  soldQuantity: number;
  totalPrice: number;
};

export type PreorderRecommendedCloseDistributionResponse = {
  vendors: PreorderCloseRecommendedDistribution[];
};

export type PreorderCloseRecommendedDistribution = {
  recommendedItems: PreorderCloseRecommendedItem[];
  vendorId: number;
  vendorName: string;
};

export type PreorderCloseRecommendedItem = {
  code: string;
  currentDiscountStock: number;
  currentStock: number;
  id: number;
  name: string;
  recommendedDiscountStockToAdd: number;
  recommendedStockToAdd: number;
  totalCurrentStock: number;
  totalRecommendedStock: number;
};

export type PreorderCloseEventRequest = {
  preorderEventId: number;
  vendorsToObtainStocks: PreorderCloseStockDistribution[];
};

export type PreorderCloseStockDistribution = {
  vendorIdToReceiveStock: number;
  itemList: PreorderCloseStockItem[];
};

export type PreorderCloseStockItem = {
  preorderItemId: number;
  quantityToRelease: number;
};

export interface PreorderItemsBulkPublishLoading {
  type: typeof PREORDER_ITEMS_BULK_PUBLISH_LOADING;
  payload: string;
}

export interface PreorderItemsBulkPublishSuccess {
  type: typeof PREORDER_ITEMS_BULK_PUBLISH_SUCCESS;
  payload: string;
}

export interface PreorderItemsBulkPublishFail {
  type: typeof PREORDER_ITEMS_BULK_PUBLISH_FAIL;
  payload: string;
}

export type PreorderEventOrder = {
  id: number;
  code: string;
  status: string;
  customerName?: string;
  email?: string;
  creationDate?: string;
  totalPrice?: number;
  processedBy?: string;
  processedByDate?: string;
  readyPickupBy?: string;
  readyPickupByDate?: string;
  completedBy?: string;
  completedDate?: string;
  notes?: any[];
  partialPayment?: PartialPayment[];
  paid?: boolean;
};

export type PartialPayment = {
  paymentAmount: number;
  paymentDate: string;
};

export type PreorderEventOrdersList = {
  content: PreorderEventOrder[];
  first: boolean;
  last: boolean;
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
};

/**
 *****************************************
 ********* Redux Action Types ************
 *****************************************
 */
interface PreorderEventsLoading {
  type: typeof PREORDER_EVENTS_LOADING;
  payload: string;
}

interface PreorderEventsFail {
  type: typeof PREORDER_EVENTS_FAIL;
  payload: string;
}

interface PreorderEventsList {
  type: typeof PREORDER_EVENTS_LIST;
  payload: PreorderEventList;
}

interface PreorderEventsRegister {
  type: typeof PREORDER_EVENTS_REGISTER;
  payload: string;
}

interface PreorderEventsRefresh {
  type: typeof PREORDER_EVENTS_REFRESH;
  payload: boolean;
}

interface PreorderEventsSetFilter {
  type: typeof PREORDER_EVENTS_SET_FILTER;
  payload: PreorderEventFilters;
}

interface PreorderEventDetailsSuccess {
  type: typeof PREORDER_EVENT_DETAILS_SUCCESS;
  payload: PreorderEventDetails;
}

interface PreorderEventArchiveSuccess {
  type: typeof PREORDER_EVENT_ARCHIVE_SUCCESS;
  payload: string;
}

interface PreorderEventUpdateSuccess {
  type: typeof PREORDER_EVENT_UPDATE_SUCCESS;
  payload: string;
}

interface PreorderItemAddSuccess {
  type: typeof PREORDER_ITEM_ADD_SUCCESS;
  payload: string;
}

interface PreorderItemUpdateSuccess {
  type: typeof PREORDER_ITEM_UPDATE_SUCCESS;
  payload: string;
}

interface PreorderItemQuantityUpdateSuccess {
  type: typeof PREORDER_ITEM_QUANTITY_UPDATE_SUCCESS;
  payload: string;
}

interface PreorderEventOrdersLoading {
  type: typeof PREORDER_EVENT_ORDERS_LOADING;
  payload: string;
}

interface PreorderEventOrdersSuccess {
  type: typeof PREORDER_EVENT_ORDERS_SUCCESS;
  payload: PreorderEventOrdersList;
}

interface PreorderEventOrdersFail {
  type: typeof PREORDER_EVENT_ORDERS_FAIL;
  payload: string;
}

interface PreorderOrderUpdateLoading {
  type: typeof PREORDER_ORDER_UPDATE_LOADING;
  payload: string;
}

interface PreorderOrderUpdateSuccess {
  type: typeof PREORDER_ORDER_UPDATE_SUCCESS;
  payload: string;
}

interface PreorderOrderUpdateFail {
  type: typeof PREORDER_ORDER_UPDATE_FAIL;
  payload: string;
}

interface PreorderEventSuggestedSuccess {
  type: typeof PREORDER_EVENT_SUGGESTED_SUCCESS;
  payload: PreorderRecommendedCloseDistributionResponse;
}

interface PreorderEventSuggestedLoading {
  type: typeof PREORDER_EVENT_SUGGESTED_LOADING;
  payload: string;
}

interface PreorderEventSuggestedFail {
  type: typeof PREORDER_EVENT_SUGGESTED_FAIL;
  payload: string;
}

interface PreorderEventSuggestedClear {
  type: typeof PREORDER_EVENT_SUGGESTED_CLEAR;
}

export type PreorderEventsDispatchTypes =
  | PreorderEventsLoading
  | PreorderEventsFail
  | PreorderEventsList
  | PreorderEventsRegister
  | PreorderEventsRefresh
  | PreorderEventsSetFilter
  | PreorderEventDetailsSuccess
  | PreorderEventArchiveSuccess
  | PreorderEventUpdateSuccess
  | PreorderItemAddSuccess
  | PreorderItemUpdateSuccess
  | PreorderItemQuantityUpdateSuccess
  | PreorderEventOrdersLoading
  | PreorderEventOrdersSuccess
  | PreorderEventOrdersFail
  | PreorderOrderUpdateLoading
  | PreorderOrderUpdateSuccess
  | PreorderOrderUpdateFail
  | PreorderEventSuggestedSuccess
  | PreorderEventSuggestedLoading
  | PreorderEventSuggestedFail
  | PreorderEventSuggestedClear
  | PreorderItemsBulkPublishLoading
  | PreorderItemsBulkPublishSuccess
  | PreorderItemsBulkPublishFail;
